import type { CareOrder, Leaves, SideEnum, SizeModeEnum } from '@talos/kyoko';
import type { CareOrderCurrency } from '../../../../components/OMS/CareOrderFormView/types';

export type CareOrderParser = 'aladdin' | 'unqork';
export type ImportCareOrderActionType = 'create' | 'modify' | 'unknown' | 'ignore';

export interface ImportedCareOrder {
  // Required care order fields
  Symbol: string | undefined;
  SizeMode?: SizeModeEnum;
  OrderQty: string;
  Currency: CareOrderCurrency;
  Side: SideEnum;
  ClOrdID: string;

  // Not required care order fields
  units?: string;
  Annotations?: CareOrder['Annotations'];
  Group?: string;
  Counterparty?: string;
  SettleDate?: string;
  SubAccount?: string;

  Comments?: string;

  // Fields related to the import flow, will be dropped before sending to backend
  action: ImportCareOrderAction;
}

export interface SimilarCareOrder {
  careOrder: CareOrder;
  rowID: string;
  /**
   * Set to true if this Similar CO is a unique match to the imported CO. This means that this similar CO must either be modified,
   * or the imported CO must be ignored (no new CO creation since it would violate the uniqueness constraint)
   */
  matchesUniquely: boolean;
  warning?: {
    /** A list of the CareOrder fields that are behind this warning */
    invalidFields: Leaves<CareOrder>[];
  };
}

/** The action interface tries to model what we should do with the care order upon import. */
export type ImportCareOrderAction = {
  similarCareOrders: SimilarCareOrder[];
  canCreateNewCareOrder: boolean;
} & ({ type: 'create' | 'unknown' | 'ignore' } | { type: 'modify'; careOrderToModify: CareOrder });

/**
 * Just a placeholder empty action (which will block importing) to put on entities if you're building them in a place
 * where you can't decide what the action to take should be yet
 */
export const EMPTY_IMPORT_CARE_ORDER_ACTION: ImportCareOrderAction = {
  similarCareOrders: [],
  type: 'unknown',
  canCreateNewCareOrder: true,
};

interface ActionMetadata {
  type: ImportCareOrderActionType;
  label: string;
  /** Description shown in the action dialog to give the user some explanation */
  actionDialogInfo: string;
  /** Description info to show in the Action column in the outer care order import dialog to tell the user whats going on with the row */
  tooltipInfo: string;
  /** Whether or not a row with this action type can be selected to be imported */
  importable: boolean;
  /** In some states we do not let the user open the action dialog menu */
  showOpenActionDialogButton: boolean;
}

export const ACTION_METADATA: { [key in ImportCareOrderActionType]: ActionMetadata } = {
  create: {
    type: 'create',
    label: 'Create New',
    actionDialogInfo: 'Import will create a new Care Order. You can change your selection below.',
    tooltipInfo:
      'Importing this row will create a new Care Order. You can change this selection in the Import Action dialog.',
    importable: true,
    showOpenActionDialogButton: true,
  },
  modify: {
    type: 'modify',
    label: 'Modify Existing',
    actionDialogInfo: 'Import will modify the selected Care Order. You can change your selection below.',
    tooltipInfo:
      'Importing this row will modify an existing Care Order. You can change the import action, as well as the Care Order to modify, in the Import Action dialog.',
    importable: true,
    showOpenActionDialogButton: true,
  },
  unknown: {
    type: 'unknown',
    label: 'Unknown',
    actionDialogInfo:
      'In order to import this Care Order you must decide to either create a new Care Order or modify an existing Care Order. Similar Care Orders eligible for modification are shown below.',
    tooltipInfo:
      'In order to import this row you must decide to either create a new Care Order or modify an existing Care Order. This is done in the Import Action dialog.',
    importable: false,
    showOpenActionDialogButton: true,
  },
  ignore: {
    type: 'ignore',
    label: 'Ignore',
    actionDialogInfo: 'An identical existing Care Order was found, and the import was set to be ignored.',
    tooltipInfo:
      "The system recognized a duplicate Care Order in the system, and set this row's import action to Ignore.",
    importable: false,
    showOpenActionDialogButton: false,
  },
};
